import { useEffect, useState, useCallback } from 'react';
import { appBadgeService } from '../services/appBadgeService';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';

interface BadgeCounts {
  unreadMessages: number;
  pendingTimeOff: number;
  pendingPayrollRequests: number;
  total: number;
}

/**
 * Hook to manage app badge count
 * Automatically updates the badge based on:
 * - Unread SMS/Email messages
 * - Pending time off requests (for admins)
 * - Pending payroll requests (for admins)
 */
export function useAppBadge() {
  const { selectedBusiness } = useBusiness();
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>({
    unreadMessages: 0,
    pendingTimeOff: 0,
    pendingPayrollRequests: 0,
    total: 0
  });
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if user is admin/manager/owner (for time off + payroll badge only; unread is same for all)
  useEffect(() => {
    const checkAdmin = async () => {
      if (!selectedBusiness?.id) return;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check business_users first
      const { data: businessUser } = await supabase
        .from('business_users')
        .select('permission_level')
        .eq('business_id', selectedBusiness.id)
        .eq('user_id', user.id)
        .maybeSingle();

      let admin = ['admin', 'manager', 'owner'].includes(businessUser?.permission_level || '');

      // Fallback: check resources table for is_admin flag
      if (!admin) {
        const { data: resource } = await supabase
          .from('resources')
          .select('is_admin')
          .eq('business_id', selectedBusiness.id)
          .eq('auth_user_id', user.id)
          .eq('status', 'active')
          .maybeSingle();
        if (resource?.is_admin === true) admin = true;
      }

      setIsAdmin(admin);
    };

    checkAdmin();
  }, [selectedBusiness?.id]);

  // Calculate total badge count
  const updateBadge = useCallback(async (counts: Partial<BadgeCounts>) => {
    setBadgeCounts(prev => {
      const newCounts = {
        unreadMessages: counts.unreadMessages ?? prev.unreadMessages,
        pendingTimeOff: counts.pendingTimeOff ?? prev.pendingTimeOff,
        pendingPayrollRequests: counts.pendingPayrollRequests ?? prev.pendingPayrollRequests,
        total: 0
      };
      
      newCounts.total = 
        newCounts.unreadMessages + 
        newCounts.pendingTimeOff + 
        newCounts.pendingPayrollRequests;

      // Update the app badge
      if (newCounts.total === 0) {
        appBadgeService.clearBadge();
      } else {
        appBadgeService.setBadge(newCounts.total);
      }

      console.log('📛 Badge updated:', newCounts);
      
      // IMPORTANT: Also notify service worker to stay in sync
      (async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && session?.access_token && selectedBusiness?.id) {
          await appBadgeService.triggerBackgroundUpdate(
            selectedBusiness.id, 
            session.user.id, 
            session.access_token
          );
        }
      })();
      
      return newCounts;
    });
  }, [selectedBusiness?.id]);

  // Refresh badge using get-badge-count edge function (single source of truth for all users)
  const refreshBadge = useCallback(async () => {
    if (!selectedBusiness?.id) {
      console.log('📛 No business selected, clearing badge');
      appBadgeService.clearBadge();
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id || !session?.access_token) {
      appBadgeService.clearBadge();
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('get-badge-count', {
        body: { business_id: selectedBusiness.id, user_id: session.user.id },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) {
        console.warn('📛 get-badge-count error:', error);
        return;
      }

      const total = data?.badge_count ?? 0;
      const breakdown = data?.breakdown ?? {};
      const unreadMessages = breakdown.unread_messages ?? 0;
      const pendingTimeOff = breakdown.pending_timeoff ?? 0;
      const pendingPayrollRequests = breakdown.pending_payroll_requests ?? 0;

      console.log('📛 Badge from get-badge-count:', { total, unreadMessages, pendingTimeOff, pendingPayrollRequests });

      updateBadge({
        unreadMessages,
        pendingTimeOff,
        pendingPayrollRequests
      });
    } catch (e) {
      console.warn('📛 get-badge-count failed:', e);
    }
  }, [selectedBusiness?.id, updateBadge]);

  // Initial load and refresh on business change
  useEffect(() => {
    refreshBadge();
    
    // Initialize background badge updates in service worker
    const initializeBackgroundUpdates = async () => {
      if (!selectedBusiness?.id) return;
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user || !session?.access_token) return;
      
      // Tell service worker to start periodic updates (even when app is closed)
      await appBadgeService.initializeBackgroundUpdates(
        selectedBusiness.id, 
        session.user.id, 
        session.access_token
      );
    };
    
    initializeBackgroundUpdates();
  }, [refreshBadge, selectedBusiness?.id]);

  // Set up real-time subscriptions for automatic updates
  useEffect(() => {
    if (!selectedBusiness?.id) return;

    // Subscribe to messages changes
    const messagesChannel = supabase
      .channel('badge-messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        () => {
          console.log('📛 Messages changed, refreshing badge...');
          refreshBadge();
        }
      )
      .subscribe();

    // Subscribe to time off requests changes (if admin)
    let timeOffChannel: any = null;
    if (isAdmin) {
      timeOffChannel = supabase
        .channel('badge-timeoff')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'time_off_requests',
            filter: `business_id=eq.${selectedBusiness.id}`
          },
          () => {
            console.log('📛 Time off requests changed, refreshing badge...');
            refreshBadge();
          }
        )
        .subscribe();
    }

    // Subscribe to payroll requests changes (if admin)
    let payrollChannel: any = null;
    if (isAdmin) {
      payrollChannel = supabase
        .channel('badge-payroll')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'payroll_request',
            filter: `business_id=eq.${selectedBusiness.id}`
          },
          () => {
            console.log('📛 Payroll requests changed, refreshing badge...');
            refreshBadge();
          }
        )
        .subscribe();
    }

    // Cleanup subscriptions
    return () => {
      messagesChannel.unsubscribe();
      if (timeOffChannel) timeOffChannel.unsubscribe();
      if (payrollChannel) payrollChannel.unsubscribe();
    };
  }, [selectedBusiness?.id, isAdmin, refreshBadge]);

  // Update badge when app visibility changes (open/close/background)
  useEffect(() => {
    if (!selectedBusiness?.id) {
      appBadgeService.clearBadge();
      return;
    }

    const handleVisibilityChange = async () => {
      console.log('📛 App visibility changed:', document.visibilityState);
      
      if (document.visibilityState === 'visible') {
        // App opened - refresh badge immediately
        console.log('📛 App opened - refreshing badge');
        await refreshBadge();
      } else if (document.visibilityState === 'hidden') {
        // App closed/backgrounded - trigger one final update
        console.log('📛 App closed - triggering final badge update');
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user && session?.access_token && selectedBusiness?.id) {
          // Tell service worker to update badge (will continue updating in background)
          await appBadgeService.triggerBackgroundUpdate(
            selectedBusiness.id, 
            session.user.id, 
            session.access_token
          );
        }
      }
    };

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Only clear if component unmounts while no business is selected
      if (!selectedBusiness?.id) {
        appBadgeService.clearBadge();
      }
    };
  }, [selectedBusiness?.id, refreshBadge]);

  // Native only: refresh badge when app comes to foreground (Capacitor appStateChange)
  useEffect(() => {
    if (!selectedBusiness?.id) return;

    let listener: { remove: () => Promise<void> } | null = null;

    const setup = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) return;
        const { App } = await import('@capacitor/app');
        listener = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) {
            console.log('📛 Native app resumed - refreshing badge');
            refreshBadge();
          }
        });
      } catch {
        // Not native or plugin not available
      }
    };

    setup();
    return () => {
      listener?.remove?.();
    };
  }, [selectedBusiness?.id, refreshBadge]);

  return {
    badgeCounts,
    refreshBadge,
    clearBadge: () => appBadgeService.clearBadge(),
    isSupported: appBadgeService.isSupported(),
    isPWA: appBadgeService.isPWA()
  };
}

